import { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames/bind";
import { withTranslation } from "../translation/";
import { undoSearch, retrySearch } from "../../state/modules/search/actions";
import { clearError, getError, ERROR_TYPE } from "../../state/modules/error";
import { ERROR_ORIGIN } from "./constants";
import TouchOutside from "../TouchOutside";
import Escapable from "../Escapable";
import styles from "./ErrorAlert.css";
import getErrorKeys from "./CopyProvider/index";

const styleContext = classNames.bind(styles);

const getContainerClasses = type =>
  type === ERROR_TYPE.network
    ? {
        networkError: true
      }
    : {};

export class ErrorAlert extends Component {
  constructor(props) {
    super(props);

    this.state = {
      errorType: props.error && props.error.type,
      errorOrigin: props.error && props.error.errorOrigin,
      actionType: props.error && props.error.actionType
    };
  }

  UNSAFE_componentWillReceiveProps({ error }) {
    if (error) {
      this.setState({
        errorType: error.type,
        errorOrigin: error.errorOrigin,
        actionType: error.actionType
      });
    }
  }

  setElement = element => {
    this.element = element;
  };

  getElement = () => this.element;

  isServerError = () => this.state.errorType === ERROR_TYPE.server;

  isVisible = () =>
    !!this.props.error && (!this.isServerError() || !this.props.isLoading);

  outsideEscapeHandler = {
    [ERROR_ORIGIN.search]: () => {
      if (this.isServerError()) {
        this.clickHandler[ERROR_ORIGIN.search]();

        return;
      }

      this.props.undoSearch();
    },
    [ERROR_ORIGIN.saveItem]: () => {
      this.props.clearError();
    }
  };

  clickHandler = {
    [ERROR_ORIGIN.search]: () => {
      this.props.retrySearch(this.state.actionType);
    },
    [ERROR_ORIGIN.saveItem]: () => {
      this.props.clearError();
    }
  };

  getErrorButtonContent = (
    { formatTranslation, error },
    { errorType, errorOrigin }
  ) => {
    const { header, details } = getErrorKeys(errorOrigin, errorType);

    if (!error) {
      return (
        <>
          <div className={styles.content}>
            <div className={styles.message}>
              <div className={styles.header}>{formatTranslation(header)}</div>
              <div className={styles.details}>{formatTranslation(details)}</div>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <Escapable
          escapeHandler={this.outsideEscapeHandler[error.errorOrigin]}
        />
        <TouchOutside
          getElement={this.getElement}
          outsideHandler={this.outsideEscapeHandler[error.errorOrigin]}
        />
        <div className={styles.content}>
          <div className={styles.message}>
            <div className={styles.header}>{formatTranslation(header)}</div>
            <div className={styles.details}>{formatTranslation(details)}</div>
          </div>
          {errorOrigin === ERROR_ORIGIN.search ? (
            <div className={styles.retry} data-auto-id="retryButton">
              {formatTranslation("plp_web_errors_retry")}
            </div>
          ) : (
            <div className={styles.close} data-auto-id="closeIcon" />
          )}
        </div>
      </>
    );
  };

  render() {
    const { errorType, errorOrigin } = this.state;

    const containerClasses = getContainerClasses(errorType);

    return (
      <button
        className={styleContext({
          container: true,
          visible: this.isVisible(),
          ...containerClasses
        })}
        ref={this.setElement}
        onClick={this.clickHandler[errorOrigin]}
        data-auto-id="genericErrorMessage"
      >
        {this.getErrorButtonContent(this.props, this.state)}
      </button>
    );
  }
}

ErrorAlert.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string.isRequired,
    type: PropTypes.number.isRequired,
    errorOrigin: PropTypes.string.isRequired,
    actionType: PropTypes.string.isRequired
  }),
  formatTranslation: PropTypes.func.isRequired,
  undoSearch: PropTypes.func.isRequired,
  retrySearch: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired
};

ErrorAlert.displayName = "ErrorAlert";

const mapStateToProps = state => ({
  error: getError(state),
  isLoading: state.search.isLoading
});

const mapDispatchToProps = {
  undoSearch,
  retrySearch,
  clearError
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation(ErrorAlert));
